import { useEffect, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { Button } from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import { reasonPhrase } from '../insights/reason-words';
import type {
  SupportFile,
  SupportFiles,
  SupportPage,
  ViewerAsk,
  ViewerTools,
} from './viewer-types';

const KIND_WORDS: Record<NonNullable<SupportFile['kind']>, string> = {
  pdf: 'PDF',
  tiff: 'TIFF',
  png: 'PNG picture',
  jpeg: 'JPEG picture',
  unknown: 'Not a PDF, TIFF, PNG or JPEG',
};

/** "1.2 MB", "84 kB". */
const sizeOf = (f: Formatter, bytes: number): string =>
  bytes >= 1_000_000
    ? `${f.decimal(bytes / 1_000_000, 1)} MB`
    : `${f.int(Math.max(1, Math.round(bytes / 1000)))} kB`;

/** "PDF by its first bytes, 1.2 MB, 3 pages". */
function factsOf(f: Formatter, file: SupportFile): string {
  const kind = file.kind ? `${KIND_WORDS[file.kind]} by its first bytes` : 'Kind not checked';
  const size = file.bytes !== null ? `, ${sizeOf(f, file.bytes)}` : '';
  const pages = `, ${f.int(file.pages.length)} ${file.pages.length === 1 ? 'page' : 'pages'}`;
  return `${kind}${size}${pages}`;
}

interface Read {
  files: SupportFiles | null;
  failed: string | null;
}

function useFiles(tools: ViewerTools, ask: ViewerAsk): Read {
  const [read, setRead] = useState<Read>({ files: null, failed: null });
  useEffect(() => {
    let current = true;
    tools.load(ask).then(
      (files) => current && setRead({ files, failed: null }),
      (e: unknown) =>
        current && setRead({ files: null, failed: e instanceof Error ? e.message : String(e) }),
    );
    return () => {
      current = false;
    };
  }, [tools, ask]);
  return read;
}

/** A page as a small picture, or, with none, its number and why it has none. */
function Thumb({
  page,
  src,
  chosen,
  onPick,
}: {
  page: SupportPage;
  src: string;
  chosen: boolean;
  onPick: () => void;
}): ReactNode {
  const turn = { '--fl-turn': `${page.rotation}deg` } as CSSProperties;
  return (
    <button
      type="button"
      className={[
        'fl-view__thumb',
        chosen && 'fl-view__thumb--at',
        page.parkedReason && 'fl-view__thumb--parked',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-pressed={chosen}
      aria-label={`Page ${page.number}${page.parkedReason ? `, stopped: ${reasonPhrase(page.parkedReason)}` : ''}`}
      onClick={onPick}
    >
      {page.rendered ? (
        <img src={src} alt="" loading="lazy" style={turn} />
      ) : (
        <span className="fl-view__blank">No picture</span>
      )}
      <span className="fl-view__no">{page.number}</span>
    </button>
  );
}

/** The chosen page, large; for a page with no picture what can be said, and the original when it is a picture itself. */
function Large({
  file,
  page,
  tools,
  ask,
}: {
  file: SupportFile;
  page: SupportPage | null;
  tools: ViewerTools;
  ask: ViewerAsk;
}): ReactNode {
  const picture = file.kind === 'png' || file.kind === 'jpeg';
  if (page?.rendered) {
    return (
      <figure className="fl-view__large">
        <img
          src={tools.renderUrl(ask, page.id)}
          alt={`Page ${page.number} of ${file.name}`}
          style={{ '--fl-turn': `${page.rotation}deg` } as CSSProperties}
        />
        <Caption page={page} />
      </figure>
    );
  }
  return (
    <figure className="fl-view__large fl-view__large--none">
      {picture && file.kept ? (
        <img src={tools.originalUrl(ask, file.name)} alt={`${file.name} as received`} />
      ) : (
        <span className="fl-view__none">
          {page
            ? `Page ${page.number} has no picture: Paige could not make one from this file.`
            : 'Paige made no pages from this file.'}
        </span>
      )}
      {page ? <Caption page={page} /> : null}
    </figure>
  );
}

function Caption({ page }: { page: SupportPage }): ReactNode {
  if (!page.parkedReason) return <figcaption>Page {page.number}</figcaption>;
  return (
    <figcaption className="fl-view__why">
      Page {page.number} stopped: {reasonPhrase(page.parkedReason)}
      {page.parkedDetail ? <span className="fl-view__detail">{page.parkedDetail}</span> : null}
    </figcaption>
  );
}

/** The file's name, what it is, and the original as received (a tab of its own), or that it is gone. */
function Top({ file, href }: { file: SupportFile; href: string }): ReactNode {
  const f = useFormat();
  return (
    <div className="fl-view__top">
      <strong className="fl-view__name">{file.name}</strong>
      <span className="fl-view__facts">{factsOf(f, file)}</span>
      {file.kept ? (
        <a className="fl-view__open" href={href} target="_blank" rel="noopener noreferrer">
          Open the original
        </a>
      ) : (
        <span className="fl-view__facts">The original is no longer kept</span>
      )}
    </div>
  );
}

/** The first page that stopped, or the first page. */
const firstOf = (file: SupportFile): number =>
  Math.max(
    0,
    file.pages.findIndex((p) => p.parkedReason !== null),
  );

/** One file: what it is, the original, its pages small and the chosen one large; arrow keys step through. */
function FileView({
  file,
  tools,
  ask,
}: {
  file: SupportFile;
  tools: ViewerTools;
  ask: ViewerAsk;
}): ReactNode {
  const [at, setAt] = useState(() => firstOf(file));
  const step = (e: KeyboardEvent) => {
    if (e.key === 'ArrowRight') setAt(Math.min(file.pages.length - 1, at + 1));
    if (e.key === 'ArrowLeft') setAt(Math.max(0, at - 1));
  };
  return (
    <section className="fl-view__file" aria-label={file.name} onKeyDown={step}>
      <Top file={file} href={tools.originalUrl(ask, file.name)} />
      <Large file={file} page={file.pages[at] ?? null} tools={tools} ask={ask} />
      {file.pages.length > 1 ? (
        <div className="fl-view__thumbs" role="group" aria-label="Pages">
          {file.pages.map((p, i) => (
            <Thumb
              key={p.id}
              page={p}
              src={tools.renderUrl(ask, p.id)}
              chosen={i === at}
              onPick={() => setAt(i)}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}

/** While the files are read, or why they could not be. */
function Status({ files, failed }: Read): ReactNode {
  if (failed)
    return (
      <p className="fl-inc__note" role="alert">
        Could not open the file: {failed}
      </p>
    );
  return files ? null : <p className="fl-inc__note">Opening the file</p>;
}

/** The file asked for (every file of the batch when it names none), and how many more came with it. */
function Files({
  files,
  tools,
  ask,
}: {
  files: SupportFiles;
  tools: ViewerTools;
  ask: ViewerAsk;
}): ReactNode {
  const asked = files.files.filter((x) => !ask.fileName || x.name === ask.fileName);
  const shown = asked.length > 0 ? asked : files.files;
  const rest = files.files.length - shown.length;
  return (
    <>
      {shown.map((file) => (
        <FileView key={file.name} file={file} tools={tools} ask={ask} />
      ))}
      {rest > 0 ? (
        <p className="fl-inc__note">
          {rest} more {rest === 1 ? 'file' : 'files'} came in the same batch.
        </p>
      ) : null}
    </>
  );
}

/**
 * A company's file, seen from the incident panel under the recorded, read-only support grant: the pages Paige
 * made of it, the original as received, and for a file Paige could not open what can be said (its kind by its
 * first bytes, its size, the opener's message). Every look is written to the company's audit trail by the API.
 */
export function FlowViewer({
  ask,
  tools,
  onBack,
}: {
  ask: ViewerAsk;
  tools: ViewerTools;
  onBack: () => void;
}): ReactNode {
  const read = useFiles(tools, ask);
  return (
    <div className="fl-view" data-viewer={ask.batchId}>
      <div className="fl-view__bar">
        <Button size="sm" variant="secondary" onClick={onBack}>
          Back to the incident
        </Button>
        <span className="fl-view__grant">
          {ask.company.name}, read-only as support. Recorded in their audit trail.
        </span>
      </div>
      <Status {...read} />
      {read.files ? <Files files={read.files} tools={tools} ask={ask} /> : null}
    </div>
  );
}
